import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { DropdownItem, DropdownMenu, DropdownToggle, Spinner, UncontrolledDropdown } from 'reactstrap';
import gql from 'graphql-tag';
import { colors } from '../../index';
import { GetUserQuery } from '../../graphql';
import Error from '../../Error';
import { signOut, useSession } from 'next-auth/react';
import { faCircleChevronRight, faChevronRight, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import config from '@cardash/config';
import { useSidebar } from '../../../contexts/SidebarContext';

const StyledDropdownMenu = styled(DropdownMenu)`
  min-width: 250px;
  left: 100% !important;
  right: auto !important;
  bottom: 10px !important;
  top: auto !important;
  transform: none !important;
  margin-left: 8px;
`;

const StyledDropdownToggle = styled(DropdownToggle)`
  display: flex;
  align-items: center;
  color: white !important;
  background: transparent !important;
  border: none !important;
  padding: ${props => (props.$collapsed ? '12px' : '12px 14px')};
  width: 100%;
  justify-content: ${props => (props.$collapsed ? 'center' : 'space-between')};
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1) !important;
  }

  &:focus,
  &:active {
    background: rgba(255, 255, 255, 0.1) !important;
    box-shadow: none !important;
  }

  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin-right: ${props => (props.$collapsed ? '0' : '12px')};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  overflow: hidden;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-right: ${props => (props.$collapsed ? '0' : '12px')};
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarInitials = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-align: left;
`;

const UserName = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 300;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RolesSection = styled.div`
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
  margin-bottom: 8px;
`;

const RolesHeader = styled(DropdownItem)`
  font-weight: 600;
  color: #666 !important;
  font-size: 16px;
  padding-bottom: 4px;
`;

const RoleItem = styled(DropdownItem)`
  color: black !important;
  font-size: 16px;
`;

const SignOutDropdownItem = styled(DropdownItem)`
  border-top: 1px solid #dee2e6;
  margin-top: 8px;
  padding-top: 12px;
  &:active {
    background-color: #696666;
  }
`;

const OpenLinkButton = styled.a`
  color: black;
  text-decoration: none;
  &:hover {
    opacity: 0.6;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  margin-top: auto;
`;

const UserDropdownWrapper = styled.div`
  margin-top: auto;
  padding: ${props => (props.$collapsed ? '8px 0' : '8px')};
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 12px;
  width: 100%;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 14px;
  margin-left: 8px;
`;

const ProfileLink = styled(Link)`
  color: black;
  text-decoration: none;
`;

const BookingAppIcon = styled(FontAwesomeIcon)`
  margin: 0 10px;
`;

const GetUser = gql`
  ${GetUserQuery}
`;

const sfacAppUrl = config.sfacAppUrl || 'https://app.stressfree.com';

const UserDropdown = () => {
  const { data: session } = useSession();
  const { isCollapsed } = useSidebar();
  const [isOpen, setIsOpen] = useState(false);

  const user = session?.user;

  const { data, loading, error } = useQuery(GetUser, {
    variables: { id: user?.id || null },
    skip: !user?.id,
  });

  const userData = useMemo(() => (data && data.user) || null, [data]);

  const userRoles = useMemo(
    () => (userData ? userData.accessLevels.map(accessLevel => accessLevel.displayName) : []),
    [userData]
  );

  if (loading)
    return (
      <LoadingWrapper>
        <Spinner size="sm" color="light" />
      </LoadingWrapper>
    );

  if (error) return <Error error={error} />;

  if (!userData) return null;

  return (
    <UserDropdownWrapper $collapsed={isCollapsed}>
      <UncontrolledDropdown isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} direction="right">
        <StyledDropdownToggle
          $collapsed={isCollapsed}
          caret={false}
          title={isCollapsed ? `${userData.firstName} ${userData.lastName}` : undefined}
        >
          <UserInfo>
            <Avatar $collapsed={isCollapsed}>
              {userData.imageUrl ? (
                <AvatarImage src={userData.imageUrl} alt="user-avatar" />
              ) : (
                <AvatarInitials>
                  {userData.firstName?.charAt(0)}
                  {userData.lastName?.charAt(0)}
                </AvatarInitials>
              )}
            </Avatar>
            {!isCollapsed && (
              <UserDetails>
                <UserName>
                  {userData.firstName} {userData.lastName}
                </UserName>
                <UserEmail>{userData.email}</UserEmail>
              </UserDetails>
            )}
          </UserInfo>
          {!isCollapsed && <ChevronIcon icon={faChevronRight} />}
        </StyledDropdownToggle>
        <StyledDropdownMenu>
          <RolesSection>
            <RolesHeader disabled>Roles</RolesHeader>
            {userRoles.map((role, index) => (
              <RoleItem key={index} disabled>
                {role}
              </RoleItem>
            ))}
          </RolesSection>
          <DropdownItem>
            <ProfileLink href={`/team/${userData.id}`}>View my profile</ProfileLink>
          </DropdownItem>
          <DropdownItem>
            <OpenLinkButton href={`${sfacAppUrl}/sfac/login`} target="_blank" rel="noreferrer">
              Go to Booking App
              <BookingAppIcon icon={faCircleChevronRight} />
            </OpenLinkButton>
          </DropdownItem>
          <SignOutDropdownItem onClick={() => signOut()}>Sign Out</SignOutDropdownItem>
        </StyledDropdownMenu>
      </UncontrolledDropdown>
    </UserDropdownWrapper>
  );
};

export default UserDropdown;
